/**
 * Funksjoner.  OA-013, 2026-09-30; OA-064, 2026-10-01.
 *
 * Which of the capabilities the mosque is ENTITLED to (by Min Masjid: its plan, an add-on, a trial) it wants switched
 * on — not "which capabilities does the mosque have". Entitlement is shown read-only: a capability that is not
 * included says so and how to get it, and has no switch. Who may operate a capability is Tilganger (permissions).
 * Everything is recorded in the platform's history and the activity log.
 * «Slå av» is two clicks whose second says it is gone for everybody (QA A13, 2026-10-16); «Slå på» stays one.
 *
 * PM gaps (2026-11-01):
 *  - B1: after a switch the menu is asked again (refreshCaps, past every cache) and the toast says where the item now is.
 *  - B2: a capability the plan includes but the privacy page does not cover yet says «venter på personvern», not
 *    «ikke inkludert», with «Gå til Personvern».
 *  - B3: an admin (settings.write) reads this page; only a super admin (capabilities.manage) switches.
 *  - B5: a reason names what is missing — the other function («Slå på den også»), or keys only Min Masjid can enter.
 *  - B9: rows grouped by the menu group they show up under, one line about what each does, «Vises under: …», and filter
 *    chips. On a phone each row stacks, so the switch is reachable without scrolling sideways.
 */
import React, { useState } from 'react';
import { can, errText, patch, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { useNav } from '../lib/nav';
import { FILTERS, canAlsoSwitch, dependency, filterOf, matches, reasonText, waitsForPrivacy, type FeatureFilter } from '../lib/features';
import { Btn, Card, LoadError, Loading, Msg, Tag, TwoStep } from '../components/ui';
import { Shell } from '../core';

const SOURCE_NO: Record<string, string> = {
  plan: 'med i abonnementet', addon: 'tillegg', trial: 'prøveperiode', manual_override: 'avtalt med Masjid',
  legacy: 'med fra før', default: 'med i grunnpakken',
};
const day = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString('nb-NO', { dateStyle: 'medium' }) : '');
/** The rows whose page is no menu item: what they change is in the member app (or not built yet). */
const APP_GROUP = { id: 'app', label_no: 'I appen' };

export function Features() {
  const { data, error, reload } = useLoad<Body>('/capabilities');
  const { go, caps, refreshCaps } = useNav();
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState<string | null>(null);
  const [filter, setFilter] = useState<FeatureFilter>('all');
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const maySwitch = data.maySwitch === undefined ? can('capabilities.manage') : Boolean(data.maySwitch);
  const all = (data.items || []) as Body[];
  const byKey = new Map(all.map((c) => [c.key, c]));
  const labelOf = (k: string) => (byKey.get(k) || { label_no: k }).label_no;
  /** Whether this person may open menu item `id` (permission and capability), so a button never leads to a 403. */
  const mayOpen = (id: string) => { const item = Shell.NAV.find((x) => x.id === id); return Boolean(item && Shell.permits(item, can) && Shell.capabilityAllows(item, caps)); };

  const flip = async (key: string, enabled: boolean) => {
    setBusy(key);
    const r = await patch(`/capabilities/${encodeURIComponent(key)}`, { enabled });
    if (!r.ok) { setBusy(null); return say(errText(r.body), 'err'); }
    // B1: the menu is built from /mosque — asked again now, so the item appears (or goes) without a reload.
    const now = await refreshCaps();
    setBusy(null);
    const place = Shell.capabilityPlace(key);
    const on = Boolean(now && now.includes(key));
    let where = '';
    if (enabled && now && !on) where = ' Den er ikke i bruk ennå: se hva den venter på i listen.';
    else if (place && place.group_no && enabled && on) where = place.gates ? ` «${place.item_no}» ligger nå under ${place.group_no}.` : ` Finnes under ${place.group_no} › ${place.item_no}.`;
    else if (place && place.gates && !enabled) where = ` «${place.item_no}» er tatt bort fra menyen.`;
    say((r.body.message_no || 'Lagret.') + where, 'ok');
    reload();
  };

  const items = all.filter((i) => i.kind !== 'provider');
  const providers = all.filter((i) => i.kind === 'provider');
  const count = (f: FeatureFilter) => items.filter((c) => matches(c, f)).length;
  const shown = items.filter((c) => matches(c, filter));
  // B9: grouped by the menu group each shows up under, in the menu's order; app-only rows last.
  const groups = [...Shell.GROUPS.filter((g) => g.label_no).map((g) => ({ id: g.id, label_no: g.label_no as string })), APP_GROUP]
    .map((g) => ({ ...g, rows: shown.filter((c) => { const p = Shell.capabilityPlace(c.key); return (p && p.group_no ? p.group : 'app') === g.id; }) }))
    .filter((g) => g.rows.length);

  const included = (c: Body) => {
    const e = c.entitlement || {};
    if (c.reason === 'blocked') return <><Tag tone="grey">ikke klar</Tag><span className="note"> · Funksjonen er bygget, men kan ikke tas i bruk før beslutningene den venter på er tatt.</span></>;
    if (e.suspended) return <><Tag tone="grey">stanset</Tag><span className="note"> · midlertidig stanset av Masjid</span></>;
    if (waitsForPrivacy(c)) {
      return <><Tag tone="gold">venter på personvern</Tag><span className="note"> · Personvernsiden må godkjennes for dette området.</span>
        {mayOpen('privacy') && <> <Btn ghost onClick={() => go('privacy')}>Gå til Personvern</Btn></>}</>;
    }
    if (c.entitled) return <><Tag tone="green">ja</Tag><span className="note"> · {SOURCE_NO[e.source] || e.source}{e.expiresAt ? ` til ${day(e.expiresAt)}` : ''}</span></>;
    return <><Tag tone="grey">nei</Tag><span className="note"> · Ikke inkludert i abonnementet. Kontakt Masjid for å aktivere denne funksjonen.</span></>;
  };
  const inUse = (c: Body) => {
    if (!c.entitled) return <span className="note">–</span>;
    const dep = dependency(c);
    const other = dep ? byKey.get(dep) : undefined;
    return <><Tag tone={c.on ? 'green' : 'grey'}>{c.on ? 'på' : 'av'}</Tag>{!c.on && c.reason ? <span className="note"> · {reasonText(c, labelOf)}</span> : null}
      {dep && canAlsoSwitch(other, maySwitch) && <> <Btn ghost disabled={busy !== null} aria-label={`Slå på ${labelOf(dep)} også`}
        onClick={() => flip(dep, true)}>{busy === dep ? 'Slår på …' : 'Slå på den også'}</Btn></>}</>;
  };
  const action = (c: Body) => {
    const e = c.entitlement || {};
    if (maySwitch && c.manageable && c.entitled && !e.suspended) {
      return c.enabled
        ? <TwoStep label="Slå av" ariaLabel={`Slå av ${c.label_no}`} busyLabel="Slår av …" disabled={busy !== null}
            confirmLabel={`Klikk igjen: «${c.label_no}» slås av for hele moskeen og alle som bruker den`} onConfirm={() => flip(c.key, false)} />
        : <Btn ghost disabled={busy !== null} aria-label={`Slå på ${c.label_no}`} onClick={() => flip(c.key, true)}>{busy === c.key ? 'Slår på …' : 'Slå på'}</Btn>;
    }
    return c.entitled && !c.manageable ? <span className="note">Styres av Masjid</span> : null;
  };
  const place = (c: Body) => {
    const p = Shell.capabilityPlace(c.key);
    return p && p.group_no ? `Vises under: ${p.group_no} › ${p.item_no}` : 'Vises i appen';
  };

  return (
    <>
      <Card title="Funksjoner" hint="Velg hvilke av funksjonene i abonnementet dere vil bruke. Hvem av de ansatte som kan bruke dem, bestemmes under Tilganger.">
        <Msg text={msg.text} tone={msg.tone} />
        <p className="field note" style={{ margin: '6px 18px' }}>
          {data.plan ? <>Abonnement: <strong>{data.plan.name}</strong>{data.plan.expiresAt ? ` (til ${day(data.plan.expiresAt)})` : ''}</> : 'Grunnpakken.'}
          {(data.addons || []).length ? <> Tillegg: {(data.addons || []).map((a: Body) => a.name + (a.expiresAt ? ` (til ${day(a.expiresAt)})` : '')).join(', ')}.</> : null}
          {' '}Hva som er inkludert, avtales med Masjid.
        </p>
        {!data.canChange && <p className="field note" style={{ margin: '6px 18px' }}>Funksjonene styres av plattformen for denne installasjonen.</p>}
        {data.canChange && !maySwitch && <p className="field note" style={{ margin: '6px 18px' }}>Du kan se funksjonene. Bare en superadministrator kan slå dem av og på.</p>}
        <div className="bnr-seg shop-filter" role="group" aria-label="Vis funksjoner">
          {FILTERS.map(([k, label]) => (
            <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}>{label} <span className="shop-count">{count(k)}</span></button>
          ))}
        </div>
        {groups.length === 0
          ? <p className="note" style={{ padding: '0 18px 14px' }}>Ingen funksjoner i dette utvalget.</p>
          : <table className="t feat-t">
            <thead><tr><th scope="col">Funksjon</th><th scope="col">Inkludert</th><th scope="col">I bruk</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
            {groups.map((g) => (
              <tbody key={g.id}>
                <tr className="feat-group"><th scope="colgroup" colSpan={4}>{g.label_no}</th></tr>
                {g.rows.map((c) => (
                  <tr key={c.key} data-filter={filterOf(c) || 'on'}>
                    <td><strong>{c.label_no}</strong>
                      {c.description_no && <span className="note" style={{ display: 'block' }}>{c.description_no}</span>}
                      <span className="note" style={{ display: 'block' }}>{place(c)}</span></td>
                    <td>{included(c)}</td>
                    <td>{inUse(c)}</td>
                    <td>{action(c)}</td>
                  </tr>))}
              </tbody>))}
          </table>}
      </Card>
      <Card title="Tilkoblede tjenester" hint="Slås på med moskeens egne avtaler og nøkler, ikke her.">
        <table className="t"><tbody>{providers.map((c: Body) => (
          <tr key={c.key}><td>{c.label_no}{c.description_no && <span className="note" style={{ display: 'block' }}>{c.description_no}</span>}</td>
            <td><Tag tone={c.on ? 'green' : 'grey'}>{c.on ? 'satt opp' : 'ikke satt opp'}</Tag></td></tr>))}</tbody></table>
      </Card>
    </>
  );
}
